import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getDashboard } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Send, CheckCircle, XCircle, Users, Megaphone, AlertCircle } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await getDashboard();
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const kpis = [
    {
      label: 'Total de Mensagens',
      value: data?.total_messages || 0,
      icon: Send,
      color: 'text-slate-600',
      bgColor: 'bg-slate-100',
    },
    {
      label: 'Mensagens Enviadas',
      value: (data?.messages_by_status?.sent || 0) + 
             (data?.messages_by_status?.delivered || 0) + 
             (data?.messages_by_status?.read || 0),
      icon: CheckCircle,
      color: 'text-teal-600',
      bgColor: 'bg-teal-100',
    },
    {
      label: 'Falhas de Envio',
      value: data?.messages_by_status?.failed || 0,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
  ];

  const summaryCards = [
    { label: 'Total de Contatos', value: data?.total_contacts || 0, icon: Users },
    { label: 'Total de Campanhas', value: data?.total_campaigns || 0, icon: Megaphone },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-[300px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDateBR = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const chartData = data?.daily_stats?.map((s) => ({
    date: formatDateBR(s._id),
    mensagens: s.count,
  })) || [];

  // Normalize status: sent/delivered/read → "Enviada", queued → "Na fila", failed → "Falha", unknown → "Desconhecido"
  const normalizeStatus = (status) => {
    if (['sent', 'delivered', 'read'].includes(status)) return 'Enviada';
    if (status === 'queued') return 'Na fila';
    if (status === 'failed') return 'Falha';
    if (status === 'unknown') return 'Desconhecido';
    return status;
  };

  // Pluralize status for tooltip
  const pluralizeStatus = (status) => {
    if (status === 'Enviada') return 'Enviadas';
    if (status === 'Na fila') return 'Na fila';
    if (status === 'Falha') return 'Falhas';
    if (status === 'Desconhecido') return 'Desconhecidos';
    return status;
  };

  const statusData = Object.entries(data?.messages_by_status || {}).reduce((acc, [key, value]) => {
    const normalizedKey = normalizeStatus(key);
    const existing = acc.find(item => item.status === normalizedKey);
    if (existing) {
      existing.total += value;
    } else {
      acc.push({ 
        status: normalizedKey, 
        total: value,
        statusPlural: pluralizeStatus(normalizedKey)
      });
    }
    return acc;
  }, []);

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="kpi-card" data-testid={`kpi-${kpi.label.toLowerCase()}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{kpi.label}</p>
                  <p className="text-3xl font-semibold mt-1">{kpi.value.toLocaleString()}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${kpi.bgColor} flex items-center justify-center`}>
                  <kpi.icon className={`w-6 h-6 ${kpi.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {summaryCards.map((card) => (
          <Card key={card.label}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                  <card.icon className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{card.label}</p>
                  <p className="text-2xl font-semibold">{card.value.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Mensagens por Dia</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280} data-testid="chart-messages-over-time">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorMensagens" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22C55E" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="mensagens"
                  stroke="#22C55E"
                  fill="url(#colorMensagens)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Mensagens por Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={statusData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
                          <p className="text-sm font-medium">{payload[0].payload.statusPlural}</p>
                          <p className="text-sm text-muted-foreground">
                            Total: <span className="font-semibold text-foreground">{payload[0].value}</span>
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="total" fill="#0F766E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mensagens Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.recent_messages?.length > 0 ? (
            <div className="space-y-3">
              {data.recent_messages.map((msg) => (
                <div
                  key={msg._id}
                  className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{msg.phone}</p>
                    <p className="text-sm text-muted-foreground truncate">{msg.message}</p>
                  </div>
                  <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                    ['sent', 'delivered', 'read'].includes(msg.delivery_status) 
                      ? 'bg-teal-100 text-teal-800' 
                      : msg.delivery_status === 'queued'
                      ? 'bg-orange-100 text-orange-800'
                      : msg.delivery_status === 'failed'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-slate-200 text-slate-800'
                  }`}>
                    {normalizeStatus(msg.delivery_status)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p className="text-muted-foreground">Nenhuma mensagem enviada ainda.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
